import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { NavigationEnd, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { AuctionStatus, AuctionType, AuctionTypeType, SessionKey } from "@shared/clickar-constants";
import { AuctionDTO } from "@shared/model/auction.model";
import { VehicleDTO } from "@shared/model/vehicle.model";
import { ApiService } from "@shared/services/api.service";
import { AuctionService } from "@shared/services/auction/auction.service";
import { StorageManagerService } from "@shared/services/storage/storage-manager.service";
import { UsersService } from "@shared/services/users/users.service";
import { ClickarUtilsService } from "@shared/services/utils/clickar-utils.service";
import { WcmService } from "@shared/services/wcm/wcm.service";
import { Subscription } from "rxjs";

@Component({
	selector: "app-auctions",
	templateUrl: "./auctions.component.html",
	styleUrl: "./auctions.component.scss",
	standalone: false
})
export class AuctionsComponent implements OnInit, OnDestroy {
	private navigationSubscription: Subscription;

	/** used to store the not-filtered auctions, to be shown on reset */
	private leasysAuctions: AuctionDTO[] = [];
	/** used to store the not-filtered auctions, to be shown on reset */
	private multiAuctions: AuctionDTO[] = [];
	/** used to store the not-filtered auctions, to be shown on reset */
	private otherAuctions: AuctionDTO[] = [];

	/** Contains the auctions to be shown: it always corresponds to {@link leasysAuctions } unless a search has been triggered */
	protected filteredLeasysAuctions: AuctionDTO[] = [];
	/** Contains the auctions to be shown: it always corresponds to {@link multiAuctions } unless a search has been triggered */
	protected filteredMultiAuctions: AuctionDTO[] = [];
	/** Contains the auctions to be shown: it always corresponds to {@link otherAuctions } unless a search has been triggered */
	protected filteredOtherAuctions: AuctionDTO[] = [];

	randomVehicles?: { [p: string]: VehicleDTO[] };

	// user: UserDTO;
	protected market = "";

	// TODO add flgClick&Buy check
	showAuctionType = false;

	runningCount = 0;
	scheduledCount = 0;

	constructor(
		public wcmService: WcmService,
		public auctionService: AuctionService,
		public router: Router,
		public dialog: MatDialog,
		public utils: ClickarUtilsService,
		public usersService: UsersService,
		public translate: TranslateService,
		public apiService: ApiService,
		public storageService: StorageManagerService
	) { }

	ngOnInit() {
		this.navigationSubscription = this.router.events.subscribe((e: any) => {
			// If it is a NavigationEnd event re-initalise the component
			if (e instanceof NavigationEnd) {
				this.refresh();
			}
		});

		this.refresh();

		if (!(this.market = this.storageService.getSessionItem(SessionKey.USER_MARKET, ""))) {
			this.usersService.getProfile()
				.subscribe(value => {
					if (value) {
						this.market = value.idMarket;
					}
				});
		}
	}

	refresh() {
		this.apiService.getRunningAuctionsUsingGET()
			.subscribe(auctions => {
				this.runningCount = auctions.filter(a => a.status === AuctionStatus.RUNNING).length;
				this.scheduledCount = auctions.filter(a => a.status === AuctionStatus.SCHEDULED).length;

				this.filterAuctions(auctions);
			});
	}

	private filterAuctions(auctions: AuctionDTO[], filtered = false) {
		const sortMethod = (a: AuctionDTO, b: AuctionDTO) => a.endDate < b.endDate ? -1 : a.endDate > b.endDate ? 1 : 0,
			auctionGroups: { [key in AuctionTypeType]: AuctionDTO[] } = {
				[AuctionType.LEASYS]: [],
				[AuctionType.MULTI]: [],
				[AuctionType.OTHER]: []
			};

		for (const auction of auctions.sort((a, b) => a.endDate < b.endDate ? -1 : a.endDate > b.endDate ? 1 : 0)) {
			auctionGroups[auction.auctionType].push(auction);
		}

		this.filteredLeasysAuctions = auctionGroups[AuctionType.LEASYS].sort(sortMethod);
		this.filteredMultiAuctions = auctionGroups[AuctionType.MULTI].sort(sortMethod);
		this.filteredOtherAuctions = auctionGroups[AuctionType.OTHER].sort(sortMethod);
		if (!filtered) { // not searching, we store the data in order to reset
			this.leasysAuctions = this.filteredLeasysAuctions;
			this.multiAuctions = this.filteredMultiAuctions;
			this.otherAuctions = this.filteredOtherAuctions;

			if (auctions.length) {
				const auctionIds = auctions.map(value1 => value1.idAuction);
				this.apiService.getRandomVehicleUsingGET(auctionIds)
					.subscribe(value1 => {
						this.randomVehicles = value1;
					});
			}
		}
	}

	ngOnDestroy() {
		if (this.navigationSubscription) {
			this.navigationSubscription.unsubscribe();
		}
	}
	/*
	goToDetails(idAuction: number) {
		if (idAuction) {
			this.router.navigate(
				[`${ClickarPath.PRIVATE}/${ClickarPath.AUCTION_DETAILS}`],
				{
					queryParams: { idAuction }
				}
			);
		}
	}

	getVehicle = (idAuction: number) => this.randomVehicles ? this.randomVehicles[idAuction] : null;

	goToShowcase() {
		this.router.navigate([`${ClickarPath.PRIVATE}/${ClickarPath.SHOWCASE_MANAGEMENT}/${ClickarPath.SHOWCASE_CATALOGUE}`]);
	}
	*/
	protected previousSearchValue = "";
	search(value: string) {
		if (this.previousSearchValue !== value) {
			this.previousSearchValue = value;
			if (value) {
				this.auctionService.searchInRunningAndScheduledAuctionsUsingGET(value)
					.subscribe(auctions => {
						this.filterAuctions(auctions, true);
						/* 	if (auctions.length == 0) {
								const dialogConfig = new MatDialogConfig(),
									data: AlertMessage = new AlertMessage();
								data.successMsg = 'success.noVehicleFound.msg';
								dialogConfig.data = data;
								dialogConfig.width = "auto";
								// dialogConfig.height = '250px';
								let dialogRef = this.dialog.open(SuccessDialogComponent, dialogConfig);
							} */
					});// TODO: cosa fare in caso di errore?
			}
			else {
				this.resetSearch();
			}
		}
	}

	protected resetSearch() {
		this.previousSearchValue = "";
		this.filteredLeasysAuctions = this.leasysAuctions;
		this.filteredMultiAuctions = this.multiAuctions;
		this.filteredOtherAuctions = this.otherAuctions;
	}

	/* getStartDate(item: AuctionDTO) {
			if (item) {
				return new Date(item.startDate);
			}
		}
	*/
}